import { forwardRef } from 'react'
import { cn } from '@/lib/utils/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Render as a square icon-only button. */
  iconOnly?: boolean
  /** Show a spinner and disable input while true. */
  loading?: boolean
  /** Monospace keyboard-shortcut chip rendered after children. */
  shortcut?: string
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: [
    'bg-accent text-on-accent border-accent',
    'hover:bg-accent-hover hover:border-accent-hover',
    'active:bg-accent-active active:border-accent-active',
  ].join(' '),
  secondary: [
    'bg-surface text-primary border-border-default',
    'hover:bg-surface-hover hover:border-border-strong',
    'active:bg-surface-active',
  ].join(' '),
  ghost: [
    'bg-transparent text-primary border-transparent',
    'hover:bg-surface-hover',
    'active:bg-surface-active',
  ].join(' '),
  danger: [
    'bg-danger text-on-accent border-danger',
    'hover:bg-danger-text hover:border-danger-text',
    'focus-visible:shadow-focus-danger',
  ].join(' '),
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-[var(--button-height-sm)] px-2.5 text-sm gap-1',
  md: 'h-[var(--button-height-md)] px-3.5 text-md gap-1.5',
  lg: 'h-[var(--button-height-lg)] px-4 text-lg gap-2',
}

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'w-[var(--button-height-sm)] h-[var(--button-height-sm)] p-0',
  md: 'w-[var(--button-height-md)] h-[var(--button-height-md)] p-0',
  lg: 'w-[var(--button-height-lg)] h-[var(--button-height-lg)] p-0',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    iconOnly = false,
    loading = false,
    shortcut,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm border font-medium leading-none cursor-pointer select-none',
        'transition-[background,border-color,color,box-shadow] duration-fast',
        'disabled:pointer-events-none disabled:opacity-50',
        variantClasses[variant],
        iconOnly ? iconSizeClasses[size] : sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : children}
      {shortcut && !loading ? <Kbd>{shortcut}</Kbd> : null}
    </button>
  )
})

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
      style={{ width: 12, height: 12 }}
    />
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="text-role-kbd-shortcut ml-1 inline-flex h-4 items-center justify-center rounded-xs px-1 font-medium opacity-70"
      style={{ background: 'var(--color-kbd-bg)' }}
      data-kbd
    >
      {children}
    </span>
  )
}
